// Payment-provider status (admin-portal) — collections plane from billing-service
// and disbursements plane from payroll-service, both via the gateway.
// NOTE: these endpoints only ever return booleans for secrets — never secret values.
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);

// ---- Collections (tenant subscription payments) ----
export interface CollectionsProviders {
  azampay: { configured: boolean; environment: string; callback_token_set: boolean };
  // Direct Vodacom M-Pesa into KaziPeople's own shortcode — presence only, never secrets.
  mpesa?: {
    configured: boolean;
    enabled: boolean;
    available: boolean;
    environment: string | null;
    source?: 'db' | 'env' | 'none';
    credential_version?: number | null;
    service_provider_code?: string | null;
  };
  stripe: { configured: boolean };
  manual: { configured: true };
}
export interface PaymentCount {
  provider: string;
  status: string;
  count: number;
}
export interface WebhookCount {
  provider: string;
  outcome: string;
  count: number;
}
export interface CollectionsStatus {
  providers: CollectionsProviders;
  payments_last_30d: PaymentCount[];
  webhooks_last_7d: WebhookCount[];
  pending_payments: number;
  // Attempts whose outcome is being confirmed with the provider (never failed, never retried).
  unknown_payments?: number;
  methods?: { wallet_provider: string | null; bank_provider: string | null };
}

// ---- Disbursements (payroll payouts) ----
export interface DisbursementStatus {
  connector: string;
  capabilities: { bulk: boolean; verify: boolean; channels: string[] };
  config: {
    checksum_key: boolean;
    checksum_order: boolean;
    webhook_secret: boolean;
    webhook_token: boolean;
    ceiling: boolean;
  };
  last_30d_counts: Record<string, number>;
  reconciliation_backlog: number;
  oldest_parked_submission: string | null;
}

export const getCollectionsStatus = () =>
  axios.get<CollectionsStatus>(`${API_V1}/billing/admin/providers`).then((r) => r.data);

// Requires the platform_owner/platform_admin ROLE — callers must treat a 403
// as "platform operators only", not as an error.
export const getDisbursementStatus = () =>
  axios
    .get<DisbursementStatus>(`${API_V1}/payroll/platform/disbursement-status`)
    .then((r) => r.data);

// Cross-tenant disbursement batches (platform), for the failed-batch drilldown.
export interface PlatformBatch {
  id: string;
  tenant_id: string;
  run_id: string | null;
  status: string;
  connector: string;
  currency: string;
  total_amount: string;
  item_count: number;
  prepared_at: string | null;
}

export const getDisbursementBatches = (status?: string) =>
  axios
    .get<{ batches: PlatformBatch[] }>(`${API_V1}/payroll/platform/disbursement-batches`, {
      params: status ? { status } : undefined,
    })
    .then((r) => r.data.batches || []);
